import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Upload,
  Eye,
  FileText,
  User,
  Calendar,
  MessageSquare,
  ArrowRight,
  RefreshCw,
  X
} from 'lucide-react';
import { apiClient, endpoints } from '../../api';
import './KYCTimeline.css';

const KYCTimeline = ({ vendorId }) => {
  const [timelineData, setTimelineData] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (vendorId) {
      fetchTimelineData();
    }
  }, [vendorId]);

  const fetchTimelineData = async () => {
    setLoading(true);
    try {
      // Fetch documents with history
      const documentsResponse = await apiClient.get(endpoints.kycDocuments(vendorId));
      if (documentsResponse.data.success) {
        setDocuments(documentsResponse.data.data);
        generateTimeline(documentsResponse.data.data);
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      setError('Failed to load timeline data');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeline = (docs) => {
    const timelineEvents = [];

    docs.forEach(doc => {
      // Add upload event
      timelineEvents.push({
        id: `upload-${doc.id}`,
        type: 'upload',
        documentType: doc.document_type,
        documentName: doc.display_name || doc.document_type.toUpperCase(),
        timestamp: doc.uploaded_at,
        status: 'completed',
        title: 'Document Uploaded',
        description: `${doc.display_name || doc.document_type.toUpperCase()} uploaded successfully`,
        details: {
          fileName: doc.original_name,
          fileSize: doc.file_size,
          documentId: doc.id
        }
      });

      // Add status change events from history
      if (doc.history && doc.history.length > 0) {
        doc.history.forEach(historyItem => {
          timelineEvents.push({
            id: `history-${doc.id}-${historyItem.created_at}`,
            type: 'status_change',
            documentType: doc.document_type,
            documentName: doc.display_name || doc.document_type.toUpperCase(),
            timestamp: historyItem.created_at,
            status: getEventStatus(historyItem.action, historyItem.new_status),
            title: getEventTitle(historyItem.action, historyItem.new_status),
            description: getEventDescription(historyItem.action, historyItem.new_status, doc.display_name),
            details: {
              action: historyItem.action,
              oldStatus: historyItem.old_status,
              newStatus: historyItem.new_status,
              notes: historyItem.notes,
              performedBy: historyItem.performed_by_type,
              documentId: doc.id
            }
          });
        });
      }

      // Add verification events
      if (doc.verification_checked_at) {
        timelineEvents.push({
          id: `verification-${doc.id}`,
          type: 'verification',
          documentType: doc.document_type,
          documentName: doc.display_name || doc.document_type.toUpperCase(),
          timestamp: doc.verification_checked_at,
          status: doc.verification_status === 'VERIFIED' ? 'completed' : 'rejected',
          title: doc.verification_status === 'VERIFIED' ? 'Document Verified' : 'Document Rejected',
          description: `${doc.display_name || doc.document_type.toUpperCase()} ${doc.verification_status.toLowerCase()}`,
          details: {
            verificationStatus: doc.verification_status,
            verificationNotes: doc.verification_notes,
            verifiedBy: doc.verified_by,
            documentId: doc.id
          }
        });
      }

      // Add expiry warnings
      if (doc.expiry_date && doc.is_expiring_soon) {
        timelineEvents.push({
          id: `expiry-warning-${doc.id}`,
          type: 'expiry_warning',
          documentType: doc.document_type,
          documentName: doc.display_name || doc.document_type.toUpperCase(),
          timestamp: new Date().toISOString(),
          status: 'warning',
          title: 'Document Expiring Soon',
          description: `${doc.display_name || doc.document_type.toUpperCase()} expires on ${formatDate(doc.expiry_date)}`,
          details: {
            expiryDate: doc.expiry_date,
            documentId: doc.id
          }
        });
      }
    });

    // Sort by timestamp (newest first)
    timelineEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setTimelineData(timelineEvents);
  };

  const getEventStatus = (action, newStatus) => {
    if (action === 'UPLOADED') return 'completed';
    if (action === 'STATUS_CHANGED') {
      if (newStatus?.includes('APPROVED') || newStatus?.includes('VERIFIED')) return 'completed';
      if (newStatus?.includes('REJECTED')) return 'rejected';
      if (newStatus?.includes('REVIEW') || newStatus?.includes('PENDING')) return 'pending';
    }
    return 'pending';
  };

  const getEventTitle = (action, newStatus) => {
    switch (action) {
      case 'UPLOADED':
        return 'Document Uploaded';
      case 'STATUS_CHANGED':
        if (newStatus?.includes('APPROVED')) return 'Document Approved';
        if (newStatus?.includes('REJECTED')) return 'Document Rejected';
        if (newStatus?.includes('REVIEW')) return 'Under Review';
        if (newStatus?.includes('VERIFIED')) return 'Document Verified';
        return 'Status Updated';
      case 'REPLACED':
        return 'Document Replaced';
      case 'DELETED':
        return 'Document Deleted';
      default:
        return 'Document Updated';
    }
  };

  const getEventDescription = (action, newStatus, documentName) => {
    switch (action) {
      case 'UPLOADED':
        return `${documentName} uploaded successfully`;
      case 'STATUS_CHANGED':
        if (newStatus?.includes('APPROVED')) return `${documentName} has been approved`;
        if (newStatus?.includes('REJECTED')) return `${documentName} has been rejected`;
        if (newStatus?.includes('REVIEW')) return `${documentName} is under review`;
        if (newStatus?.includes('VERIFIED')) return `${documentName} has been verified`;
        return `${documentName} status updated`;
      case 'REPLACED':
        return `${documentName} has been replaced with a new version`;
      case 'DELETED':
        return `${documentName} has been deleted`;
      default:
        return `${documentName} has been updated`;
    }
  };

  const getEventIcon = (type, status) => {
    switch (type) {
      case 'upload':
        return <Upload size={20} className="timeline-icon upload" />;
      case 'verification':
        return status === 'completed' 
          ? <CheckCircle size={20} className="timeline-icon verified" />
          : <XCircle size={20} className="timeline-icon rejected" />;
      case 'status_change':
        if (status === 'completed') return <CheckCircle size={20} className="timeline-icon verified" />;
        if (status === 'rejected') return <XCircle size={20} className="timeline-icon rejected" />;
        if (status === 'pending') return <Clock size={20} className="timeline-icon pending" />;
        return <FileText size={20} className="timeline-icon default" />;
      case 'expiry_warning':
        return <AlertTriangle size={20} className="timeline-icon warning" />;
      default:
        return <FileText size={20} className="timeline-icon default" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'completed': 'status-badge verified',
      'pending': 'status-badge pending',
      'rejected': 'status-badge rejected',
      'warning': 'status-badge warning'
    };

    const statusLabels = {
      'completed': 'Completed',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'warning': 'Warning'
    };

    return (
      <span className={statusClasses[status] || 'status-badge default'}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeColor = (docType) => {
    const colors = {
      'pan': '#3b82f6',
      'aadhaar': '#10b981',
      'gst': '#f59e0b',
      'fssai': '#8b5cf6',
      'shopLicense': '#ef4444',
      'bankProof': '#6b7280'
    };
    return colors[docType] || '#6b7280';
  };

  const filteredTimeline = timelineData.filter(event => {
    if (filterStatus === 'all') return true;
    return event.status === filterStatus;
  });

  const handleDocumentView = (documentId) => {
    const doc = documents.find(d => d.id === documentId);
    if (doc) {
      setSelectedDocument(doc);
    }
  };

  if (loading) {
    return (
      <div className="timeline-loading">
        <div className="loading-spinner"></div>
        <p>Loading timeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timeline-error">
        <XCircle size={48} />
        <h3>Error Loading Timeline</h3>
        <p>{error}</p>
        <button onClick={fetchTimelineData} className="retry-btn">
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="kyc-timeline">
      <div className="timeline-header">
        <div className="header-content">
          <h2>KYC Verification Timeline</h2>
          <p>Track the progress of your document verification process</p>
        </div>
        
        <div className="timeline-filters">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Events</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="warning">Warnings</option>
          </select>
          <button onClick={fetchTimelineData} className="refresh-btn">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {filteredTimeline.length === 0 ? (
        <div className="timeline-empty">
          <Clock size={48} />
          <h3>No Timeline Events</h3>
          <p>
            {filterStatus === 'all' 
              ? 'Upload your first document to start the KYC verification process.'
              : `No events found with status: ${filterStatus}`
            }
          </p>
        </div>
      ) : (
        <div className="timeline-container">
          <div className="timeline-line"></div>
          
          {filteredTimeline.map((event, index) => (
            <div key={event.id} className={`timeline-item ${event.status}`}>
              <div className="timeline-marker">
                {getEventIcon(event.type, event.status)}
              </div>
              
              <div className="timeline-content">
                <div className="timeline-header-item">
                  <div className="timeline-title-section">
                    <h4 className="timeline-title">{event.title}</h4>
                    <div className="timeline-meta">
                      <span 
                        className="document-type-badge"
                        style={{ backgroundColor: getDocumentTypeColor(event.documentType) }}
                      >
                        {event.documentName}
                      </span>
                      {getStatusBadge(event.status)}
                    </div>
                  </div>
                  <div className="timeline-timestamp">
                    <Calendar size={14} />
                    <span>{formatDate(event.timestamp)}</span>
                  </div>
                </div>
                
                <div className="timeline-description">
                  <p>{event.description}</p>
                </div>

                {event.details && (
                  <div className="timeline-details">
                    {event.details.fileName && (
                      <div className="detail-item">
                        <FileText size={14} />
                        <span>File: {event.details.fileName}</span>
                        {event.details.fileSize && (
                          <span className="file-size">({formatFileSize(event.details.fileSize)})</span>
                        )}
                      </div>
                    )}
                    
                    {event.details.performedBy && (
                      <div className="detail-item">
                        <User size={14} />
                        <span>By: {event.details.performedBy}</span>
                      </div>
                    )}
                    
                    {event.details.notes && (
                      <div className="detail-item notes">
                        <MessageSquare size={14} />
                        <span>Notes: {event.details.notes}</span>
                      </div>
                    )}
                    
                    {event.details.verificationNotes && (
                      <div className="detail-item notes">
                        <MessageSquare size={14} />
                        <span>Review Notes: {event.details.verificationNotes}</span>
                      </div>
                    )}
                    
                    {event.details.expiryDate && (
                      <div className="detail-item expiry">
                        <AlertTriangle size={14} />
                        <span>Expires: {formatDate(event.details.expiryDate)}</span>
                      </div>
                    )}
                  </div>
                )}

                {event.details?.documentId && (
                  <div className="timeline-actions">
                    <button 
                      onClick={() => handleDocumentView(event.details.documentId)}
                      className="action-btn view"
                    >
                      <Eye size={14} />
                      View Document
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document Detail Modal */}
      {selectedDocument && (
        <div className="document-modal">
          <div className="modal-overlay" onClick={() => setSelectedDocument(null)}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{selectedDocument.display_name || selectedDocument.document_type.toUpperCase()} Details</h3>
              <button onClick={() => setSelectedDocument(null)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="document-info-grid">
                <div className="info-item">
                  <label>Document Type:</label>
                  <span>{selectedDocument.display_name || selectedDocument.document_type.toUpperCase()}</span>
                </div>
                
                <div className="info-item">
                  <label>File Name:</label>
                  <span>{selectedDocument.original_name}</span>
                </div>
                
                <div className="info-item">
                  <label>File Size:</label>
                  <span>{formatFileSize(selectedDocument.file_size)}</span>
                </div>
                
                <div className="info-item">
                  <label>Uploaded:</label>
                  <span>{formatDate(selectedDocument.uploaded_at)}</span>
                </div>
                
                <div className="info-item">
                  <label>Status:</label>
                  <span>{getStatusBadge(selectedDocument.verification_status === 'VERIFIED' ? 'completed' : 'pending')}</span>
                </div>
                
                {selectedDocument.expiry_date && (
                  <div className="info-item">
                    <label>Expiry Date:</label>
                    <span className={selectedDocument.is_expired ? 'expired' : ''}>
                      {formatDate(selectedDocument.expiry_date)}
                    </span>
                  </div>
                )}
                
                {selectedDocument.verification_notes && (
                  <div className="info-item full-width">
                    <label>Verification Notes:</label>
                    <span>{selectedDocument.verification_notes}</span>
                  </div>
                )}
              </div>

              {selectedDocument.history && selectedDocument.history.length > 0 && (
                <div className="document-history">
                  <h4>Document History</h4>
                  <div className="history-list">
                    {selectedDocument.history.map((hist, idx) => (
                      <div key={idx} className="history-item">
                        <div className="history-header">
                          <span className="history-action">{hist.action}</span>
                          <span className="history-date">{formatDate(hist.created_at)}</span>
                        </div>
                        {hist.notes && (
                          <div className="history-notes">{hist.notes}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCTimeline;
